// Switcher between view and edit modes for a single item row. Holds the
// edit draft + busy state, fires PATCH on save and DELETE on remove, then
// hands the result back up to ItemsTable. Errors surface inline as an
// extra <tr> beneath the editor so they don't disappear silently.
import { useState, Fragment } from "react";
import { api } from "../../../lib/api";
import type { TakeoffItemDto } from "../../../../../shared/types/pipe-takeoff";
import { ItemRowView } from "./ItemRowView";
import { ItemRowEdit } from "./ItemRowEdit";

interface Props {
  projectId: number;
  item: TakeoffItemDto;
  onChanged: (next: TakeoffItemDto) => void;
  onDeleted: (id: number) => void;
}

// null clears the column on the server; undefined would be dropped by
// JSON.stringify and leave the prior DB value untouched.
function nullable(s: string | undefined): string | null {
  return s && s.length > 0 ? s : null;
}

export function ItemRow({ projectId, item, onChanged, onDeleted }: Props) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(item);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const updated = await api<TakeoffItemDto>(
        `/api/tools/pipe-takeoff/projects/${projectId}/items/${item.id}`,
        {
          method: "PATCH",
          json: {
            category: draft.category,
            description: draft.description,
            sizeDn: nullable(draft.sizeDn),
            sizeSecondary: nullable(draft.sizeSecondary),
            material: nullable(draft.material),
            schedule: nullable(draft.schedule),
            rollGroovedEnds: draft.rollGroovedEnds,
            lengthM: draft.lengthM ?? null,
            quantity: draft.quantity,
            unit: nullable(draft.unit),
            sectionId: nullable(draft.sectionId),
          },
        },
      );
      onChanged(updated);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${item.description}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      await api(
        `/api/tools/pipe-takeoff/projects/${projectId}/items/${item.id}`,
        { method: "DELETE" },
      );
      onDeleted(item.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const errorRow = error && (
    <tr>
      <td colSpan={9} style={{ background: "var(--bsp-red-tint)", color: "var(--bsp-red-fg)", fontSize: 12 }}>
        {error}
      </td>
    </tr>
  );

  if (editing) {
    return (
      <Fragment>
        <ItemRowEdit
          draft={draft}
          busy={busy}
          onChange={setDraft}
          onSave={() => void save()}
          onCancel={() => {
            setDraft(item);
            setEditing(false);
            setError(null);
          }}
        />
        {errorRow}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <ItemRowView
        item={item}
        busy={busy}
        onEdit={() => {
          setDraft(item);
          setError(null);
          setEditing(true);
        }}
        onDelete={() => void remove()}
      />
      {errorRow}
    </Fragment>
  );
}
